import { useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Popover,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { OCC_REPORT_TYPE_LABELS, REPORT_TYPES, type ReportType } from '../../types/documentTypes.generated';
import { AcaoEmLink } from '../applications/AcaoEmLink';
import { rotuloCurtoDoDocumento } from './marcacaoDeDocumentos';

/**
 * Os grupos da lista de documentos, na ordem em que a equipe pensa neles. A lista de tipos vem do
 * sistema (`REPORT_TYPES`, gerado do backend); um tipo novo sem grupo aqui cai em "Outros".
 */
const GRUPOS_DE_DOCUMENTO: { titulo: string; tipos: readonly string[] }[] = [
  { titulo: 'Programas', tipos: ['pgr', 'pcmso', 'pcmso_analitico', 'pca', 'ppr', 'pcmat'] },
  { titulo: 'Laudos e avaliações', tipos: ['ltcat', 'lip', 'lp', 'aet', 'apr', 'quadro_insalubridade', 'nr12', 'nr10', 'nr23', 'nr20'] },
  { titulo: 'eSocial', tipos: ['esocial_s2210', 'esocial_s2220', 'esocial_s2240'] },
  { titulo: 'Atestados e perfis', tipos: ['aso', 'ppp'] },
  { titulo: 'Planos e ordens', tipos: ['plano_acao', 'plano_acao_cipa', 'os'] },
];

export function gruposDeDocumento(): { titulo: string; tipos: ReportType[] }[] {
  const agrupados = GRUPOS_DE_DOCUMENTO.map((grupo) => ({
    titulo: grupo.titulo,
    tipos: REPORT_TYPES.filter((tipo) => grupo.tipos.includes(tipo)),
  }));
  const outros = REPORT_TYPES.filter((tipo) => !GRUPOS_DE_DOCUMENTO.some((grupo) => grupo.tipos.includes(tipo)));
  return [...agrupados, { titulo: 'Outros', tipos: outros }].filter((grupo) => grupo.tipos.length > 0);
}

/** "Só em: PGR, PCMSO +2": as duas primeiras siglas e quantas mais. */
export function resumoDosDocumentos(tipos: readonly ReportType[]): string {
  const siglas = tipos.slice(0, 2).map(rotuloCurtoDoDocumento).join(', ');
  return tipos.length > 2 ? `${siglas} +${tipos.length - 2}` : siglas;
}

/** O nome por extenso, sem repetir a sigla que a linha já mostra em negrito: "PGR" + "Programa de…". */
function descricaoDoDocumento(tipo: ReportType): string {
  const rotulo = OCC_REPORT_TYPE_LABELS[tipo];
  const sigla = rotuloCurtoDoDocumento(tipo);
  if (rotulo === sigla) return '';
  const entreParenteses = /^\S+ \(([^)]*)\)$/.exec(rotulo);
  return entreParenteses && rotulo.startsWith(sigla) ? entreParenteses[1] : rotulo;
}

/** A bolinha azul com quantos estão marcados, como no desenho aprovado. */
function Contagem({ valor }: { valor: number }) {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 26,
        height: 26,
        px: 0.5,
        borderRadius: 13,
        bgcolor: '#1d4ed8',
        color: '#fff',
        fontSize: 14,
        fontWeight: 700,
      }}
    >
      {valor}
    </Box>
  );
}

/** A moldura de cada uma das duas respostas: a escolhida ganha borda na cor do sistema. */
function moldura(ligada: boolean) {
  return {
    border: 2,
    borderColor: ligada ? 'primary.main' : 'grey.400',
    borderRadius: 2.5,
    bgcolor: ligada ? '#fffafc' : 'background.paper',
    px: 1.75,
    py: 1.25,
  };
}

export interface EscolhaDeDocumentosProps {
  /** false: "Sai em todos os documentos"; true: "Só nestes documentos", com os `escolhidos`. */
  soEstes: boolean;
  escolhidos: readonly ReportType[];
  /** A marcação da maioria dos riscos do destino; vazia esconde "Marcar como a maioria". */
  sugestao?: readonly ReportType[];
  aoMudar: (soEstes: boolean, escolhidos: ReportType[]) => void;
}

/**
 * As duas respostas — "Sai em todos" ou "Só nestes" com a lista agrupada sempre à vista. É a mesma
 * escolha na célula de um risco e na ação em lote, para que as duas digam a mesma coisa.
 */
export function EscolhaDeDocumentos({ soEstes, escolhidos, sugestao, aoMudar }: EscolhaDeDocumentosProps) {
  const [busca, setBusca] = useState('');
  // A lista fica sempre à vista: marcar um documento já é responder "Só nestes"; desmarcar o
  // último volta a "Sai em todos".
  const escolher = (novos: ReportType[]) => aoMudar(novos.length > 0, novos);
  const alternar = (tipo: ReportType) =>
    escolher(escolhidos.includes(tipo) ? escolhidos.filter((t) => t !== tipo) : [...escolhidos, tipo]);
  const setSoEstes = (novo: boolean) => aoMudar(novo, [...escolhidos]);

  const termo = busca.trim().toLocaleLowerCase('pt-BR');
  const grupos = gruposDeDocumento()
    .map((grupo) => ({
      ...grupo,
      tipos: grupo.tipos.filter(
        (tipo) => !termo || OCC_REPORT_TYPE_LABELS[tipo].toLocaleLowerCase('pt-BR').includes(termo)
      ),
    }))
    .filter((grupo) => grupo.tipos.length > 0);

  return (
    <Stack spacing={1.5}>
      <Box component="label" sx={{ ...moldura(!soEstes), display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}>
        <Radio
          checked={!soEstes}
          onChange={() => setSoEstes(false)}
          value="todos"
          inputProps={{ 'aria-label': 'Sai em todos os documentos (recomendado)' }}
          sx={{ p: 0 }}
        />
        <Typography sx={{ fontSize: 16 }}>
          <Box component="span" fontWeight={600}>
            Sai em todos os documentos
          </Box>{' '}
          <Box component="span" sx={{ color: 'success.dark', fontWeight: 600, fontSize: 14 }}>
            (recomendado)
          </Box>
        </Typography>
      </Box>

      <Box sx={{ ...moldura(soEstes), display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}>
          <Radio
            checked={soEstes}
            onChange={() => setSoEstes(true)}
            value="estes"
            inputProps={{ 'aria-label': 'Só nestes documentos:' }}
            sx={{ p: 0 }}
          />
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Só nestes documentos:</Typography>
          {escolhidos.length > 0 && <Contagem valor={escolhidos.length} />}
        </Box>

        {sugestao && sugestao.length > 0 && (
          <Box>
            <AcaoEmLink onClick={() => escolher([...sugestao])}>
              Marcar como a maioria ({sugestao.map(rotuloCurtoDoDocumento).join(', ')})
            </AcaoEmLink>
          </Box>
        )}
        <TextField
          fullWidth
          type="search"
          placeholder="Buscar documento"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          inputProps={{ 'aria-label': 'Buscar documento' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            sx: { height: 44, borderRadius: 2 },
          }}
        />
        {/* Com "Sai em todos", a lista fica à vista e esmaecida, e marcar nela já restringe. */}
        <Box
          role="group"
          aria-label="Documentos"
          data-esmaecida={soEstes ? undefined : 'true'}
          sx={{
            maxHeight: 250,
            overflowY: 'auto',
            border: 1,
            borderColor: 'grey.300',
            borderRadius: 2,
            opacity: soEstes ? 1 : 0.55,
          }}
        >
          {grupos.map((grupo) => (
            <Box key={grupo.titulo}>
              <Typography
                component="h4"
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'grey.800',
                  bgcolor: 'grey.100',
                  borderBottom: 1,
                  borderColor: 'grey.300',
                  px: 1.75,
                  height: 30,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {`${grupo.titulo} · ${grupo.tipos.filter((tipo) => escolhidos.includes(tipo)).length} de ${grupo.tipos.length}`}
              </Typography>
              {grupo.tipos.map((tipo) => {
                const marcado = escolhidos.includes(tipo);
                return (
                  <Box
                    component="label"
                    key={tipo}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      minHeight: 40,
                      px: 1.75,
                      py: 0.5,
                      borderBottom: 1,
                      borderColor: 'grey.100',
                      cursor: 'pointer',
                      bgcolor: marcado ? '#fcf3f7' : undefined,
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={marcado}
                      onChange={() => alternar(tipo)}
                      inputProps={{ 'aria-label': rotuloCurtoDoDocumento(tipo) }}
                      sx={{ p: 0 }}
                    />
                    <Typography variant="body2" component="span" color="grey.800">
                      <Box component="span" fontWeight={700} color="text.primary" sx={{ whiteSpace: 'nowrap', mr: 1 }}>
                        {rotuloCurtoDoDocumento(tipo)}
                      </Box>
                      {descricaoDoDocumento(tipo)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Stack>
  );
}

export interface SeletorDeDocumentosProps {
  nomeDoRisco: string;
  marcados: readonly ReportType[];
  /** A marcação da maioria dos riscos do destino; vazia esconde "Marcar como a maioria". */
  sugestao?: readonly ReportType[];
  somenteLeitura: boolean;
  aoSalvar: (tipos: ReportType[]) => void;
}

/**
 * A coluna Documentos do inventário de riscos (#606, variante 2): o caso comum não pede nada —
 * o risco sem documento marcado sai em todos os documentos. Restringir é a exceção: o seletor abre
 * ancorado na célula, com a lista agrupada sempre à vista. Restringir um risco não muda onde os
 * outros saem.
 */
export function SeletorDeDocumentos({
  nomeDoRisco,
  marcados,
  sugestao,
  somenteLeitura,
  aoSalvar,
}: SeletorDeDocumentosProps) {
  const [ancora, setAncora] = useState<HTMLElement | null>(null);
  const [soEstes, setSoEstes] = useState(marcados.length > 0);
  const [escolhidos, setEscolhidos] = useState<ReportType[]>([...marcados]);

  const abrir = (evento: React.MouseEvent<HTMLElement>) => {
    setSoEstes(marcados.length > 0);
    setEscolhidos([...marcados]);
    setAncora(evento.currentTarget);
  };
  const fechar = () => setAncora(null);
  const salvar = () => {
    aoSalvar(soEstes ? escolhidos : []);
    fechar();
  };
  const restringido = marcados.length > 0;
  const rotuloDoBotao = restringido ? 'Alterar' : 'Restringir';

  let resumo: React.ReactNode;
  if (restringido) {
    resumo = (
      <Typography variant="body2" fontWeight={600} color="text.primary">
        {`Só em: ${marcados.slice(0, 2).map(rotuloCurtoDoDocumento).join(', ')}`}
        {marcados.length > 2 && (
          <Box component="span" sx={{ ml: 0.5, px: 0.875, borderRadius: 2.5, bgcolor: 'grey.200', color: 'grey.800', fontSize: 12 }}>
            {`+${marcados.length - 2}`}
          </Box>
        )}
      </Typography>
    );
  } else {
    resumo = (
      <Stack direction="row" spacing={0.75} alignItems="center">
        <CheckIcon aria-hidden sx={{ fontSize: 16, color: 'grey.600' }} />
        <Typography variant="body2" color="text.secondary">
          Sai em todos
        </Typography>
      </Stack>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {resumo}
        {!somenteLeitura && (
          <AcaoEmLink onClick={abrir} aria-label={`${rotuloDoBotao} os documentos de ${nomeDoRisco}`}>
            {rotuloDoBotao}
          </AcaoEmLink>
        )}
      </Stack>

      <Popover
        open={ancora !== null}
        anchorEl={ancora}
        onClose={fechar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { width: 460, maxWidth: '100vw', borderRadius: 3 } }}
      >
        <Box
          component="section"
          aria-label={`Documentos em que ${nomeDoRisco} sai`}
          sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, px: 2, pt: 1.75 }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography component="h3" sx={{ fontSize: 16, fontWeight: 600 }}>
              {`Documentos em que ${nomeDoRisco} sai`}
            </Typography>
            <IconButton aria-label="Fechar" onClick={fechar}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <EscolhaDeDocumentos
            soEstes={soEstes}
            escolhidos={escolhidos}
            sugestao={sugestao}
            aoMudar={(novoSoEstes, novos) => {
              setSoEstes(novoSoEstes);
              setEscolhidos(novos);
            }}
          />

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ borderTop: 1, borderColor: 'grey.300', pt: 1.5, pb: 1.75 }}
          >
            <Typography variant="body2" sx={{ fontSize: 13, color: 'grey.800' }}>
              {`${escolhidos.length} de ${REPORT_TYPES.length} marcados`}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button onClick={fechar}>Cancelar</Button>
              <Button variant="contained" onClick={salvar} disabled={soEstes && escolhidos.length === 0}>
                Salvar
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
}
